/* eslint-disable */
// eslint-disable-next-line max-classes-per-file
import { Checkbox, Button } from '@ipc/ipc-components';
import update from 'immutability-helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import moment from 'moment';
import { faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import locale from 'date-fns/locale/en-US';
import {
  Grid, MenuItem, Select,
} from '@material-ui/core';
import fieldFormatter from './fieldFormatter';
import fieldFilters from './fieldFilters';
import { sortValues } from './sortValues';

class Column {
  constructor(header, accessor, sortable = true, parameters) {
    this.Header = header;
    this.accessor = accessor;
    this.sortable = sortable;
    this.parameters = parameters;
  }

  headerClassName = 'wordwrap';

  withWidth(width) {
    this.width = width;
    return this;
  }

  withShow(show) {
    this.show = show;
    return this;
  }

  withGetProps(getProps) {
    this.getProps = getProps;
    return this;
  }
}

export default (tableData) => {
  const fieldFiltersWithData = fieldFilters(tableData);

  class FilteredNullableBooleanColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('boolean');
      Object.assign(this, fieldFiltersWithData.applyNullableBooleanFilter(this));
    }
  }

  class FilteredTextColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
      this.className = 'wordwrap';
    }
  }

  class FilteredDateColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('date');
      Object.assign(this, fieldFiltersWithData.applyDateFilter(this));
    }
  }

  class FilteredTextCommaColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('commaText');
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
      this.className = 'wordwrap';
    }
  }

  class NotFilteredBooleanColumn extends Column {
    constructor(header, accessor, sortable, conditionalHide = false) {
      super(header, accessor, sortable);
      this.Cell = (row) => (conditionalHide && conditionalHide(row) ? '' : fieldFormatter('boolean')(row));
    }
  }

  class NotFilteredDateColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('date');
    }
  }

  class NotFilteredDateWithDayColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateWithDay');
    }
  }

  class NotFilteredDateTimeColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateTime');
    }
  }

  class NotFilteredDateTimeWithSecondsColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateTimeWithSeconds');
    }
  }

  class NotFilteredTextColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('nonZeroText');
    }
  }

  class NotFilteredTextCommaColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('commaText');
    }
  }

  class NotFilteredNumberColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.sortMethod = sortValues;
      this.className = '-column-align-right';
      this.Cell = fieldFormatter('number');
    }
  }

  class NotFilteredPercentageColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('percentage');
      this.sortMethod = sortValues;
      this.className = '-column-align-right';
    }
  }

  class CheckboxColumn extends Column {
    constructor(header, accessor, sortable, parameters) {
      super(header, accessor, sortable, parameters);
      this.Header = (
        <div className="checkbox-column">
          <input
            checked={parameters.isHeaderChecked}
            type="checkbox"
            className="form-check-input"
            id="header-checkbox"
            onClick={() => parameters.selectAll()}
          />
        </div>
      );
      this.Cell = ((row) => (
        <div className="checkbox-column">
          <input
            type="checkbox"
            checked={parameters.rowChecked(row)}
            className="form-check-input"
            id={parameters.identifier(row)}
            disabled={parameters.isCheckboxDisabled ? parameters.isCheckboxDisabled(row) : false}
            onClick={() => (parameters.isCheckboxDisabled && parameters.isCheckboxDisabled(row) ? null : parameters.updateSelectedRecords(row))}
          />
        </div>
      ));
      this.width = 25;
      this.sortable = false;
      this.resizable = false;
    }
  }

  class ActionColumn extends Column {
    constructor(actionList = []) {
      super();
      const EditButton = <FontAwesomeIcon icon={faPencilAlt} className="icon" aria-hidden="true" />;
      const DeleteButton = <FontAwesomeIcon icon={faTrash} className="icon" aria-hidden="true" />;
      this.Header = 'Actions';
      this.Cell = (row) => (
        <div className="table-action-buttons">
          {
            actionList.map((action) => {
              const [actionName, accessor, actionFunction, displayButton, conditionalDisplay] = action;
              if (!displayButton) return '';
              if (conditionalDisplay && !conditionalDisplay(row)) return '';
              switch (actionName) {
              // other type of action buttons can be added
              case 'EDIT':
                return (
                  <Button
                    key={`row_edit_${row.original[accessor]}`}
                    onClick={() => actionFunction.apply(null, [row.original])}
                  >
                    {EditButton}
                  </Button>
                );
              case 'DELETE':
                return (
                  <Button
                    key={`row_delete_${row.original[accessor]}`}
                    onClick={() => actionFunction.apply(null, [row.original])}
                  >
                    {DeleteButton}
                  </Button>
                );
              default:
                return <></>;
              }
            })
          }
        </div>
      );
    }
  }

  class EditableCheckboxColumn extends Column {
    constructor(header, accessor, sortable = true, data = [], setData = () => {}, conditionalHide = false) {
      super(header, accessor, sortable);
      this.className = 'column-center-align';

      const handleDataChange = (cellInfo, event) => {
        setData(update(data, {
          [cellInfo.index]: {
            [cellInfo.column.id]: {
              $set: event.target.checked,
            },
          },
        }));
      };

      const renderEditableCheckbox = (cellInfo) => {
        const cellValue = data[cellInfo.index][cellInfo.column.id] === undefined ? false : data[cellInfo.index][cellInfo.column.id];
        return conditionalHide && conditionalHide(cellInfo) ? '' : (
          <Checkbox
            checked={cellValue}
            onChange={(e) => handleDataChange(cellInfo, e)}
          />
        );
      };

      this.Cell = data.length > 0 ? renderEditableCheckbox : '';
    }
  }

  class EditableDatePickerColumn extends Column {
    constructor(header, accessor, sortable = true, data = [], setData = () => {}, propsPopulator = () => {}) {
      super(header, accessor, sortable);
      this.className = 'column-center-align';

      const handleDateChange = async (cellInfo, date) => {
        const cellValue = data[cellInfo.index][cellInfo.column.id];
        if (moment(date).isValid()) {
          setData(update(data, {
            [cellInfo.index]: {
              [cellInfo.column.id]: {
                $set: moment(date),
              },
            },
          }));
        } else if (cellValue !== null) {
          setData(update(data, {
            [cellInfo.index]: {
              [cellInfo.column.id]: {
                $set: null,
              },
            },
          }));
        }
      };

      const renderEditableDatePicker = (cellInfo) => {
        const cellValue = data[cellInfo.index][cellInfo.column.id];
        return (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
            <Grid container>
              <KeyboardDatePicker
                disableToolbar
                autoOk
                format="dd-MM-yyyy"
                margin="normal"
                id={`date-picker-${accessor}`}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                value={cellValue}
                onChange={(e) => handleDateChange(cellInfo, e)}
                {...propsPopulator(data[cellInfo.index])}
              />
            </Grid>
          </MuiPickersUtilsProvider>
        );
      };

      this.Cell = data.length > 0 ? renderEditableDatePicker : '';
    }
  }

  class NotFilteredReadOnlyCheckboxColumn extends Column {
    constructor(header, accessor, sortable = true, conditionalHide = false) {
      super(header, accessor, sortable);
      this.className = 'column-center-align';

      const renderCheckbox = (cellInfo) => (
        <div className="readonly-checkbox-column">
          <Checkbox
            checked={cellInfo.value}
            className="readonly-checkbox"
          />
        </div>
      );
      this.Cell = (row) => (conditionalHide && conditionalHide(row) ? '' : renderCheckbox(row));
    }
  }

  class EditableSelectColumn extends Column {
    constructor(header, accessor, sortable = true, data = [], setData = () => {}, populateOptions = () => []) {
      super(header, accessor, sortable);
      this.className = 'column-center-align';

      const handleDataChange = (cellInfo, event) => {
        const cellValue = data[cellInfo.index][cellInfo.column.id];
        if (cellValue) {
          setData(update(data, {
            [cellInfo.index]: {
              [cellInfo.column.id]: {
                $set: event.target.value,
              },
            },
          }));
        }
      };
      const renderSelect = (cellInfo) => {
        const cellValue = data[cellInfo.index][cellInfo.column.id];
        const options = populateOptions(cellInfo);
        return (
          <Select
            autoWidth={false}
            value={cellValue}
            placeholder={cellValue}
            onChange={(e) => handleDataChange(cellInfo, e)}
          >
            {options.map((item) => (
              <MenuItem value={item}>{item}</MenuItem>))}
          </Select>
        );
      };

      this.Cell = (row) => renderSelect(row);
    }
  }

  class EditableInputColumn extends Column {
    constructor(header, accessor, sortable = true, data = [], setData = () => {}, validateInput) {
      super(header, accessor, sortable);
      this.className = 'column-center-align';
      const renderSelect = (cellInfo) => (
        <div
          style={{ backgroundColor: '#fafafa', width: '50px' }}
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => {
            setData(update(data, {
              [cellInfo.index]: {
                [cellInfo.column.id]: {
                  $set: validateInput ? validateInput(e.target.innerText) : e.target.innerText,
                },
              },
            }));
          }}
          dangerouslySetInnerHTML={{
            __html: data[cellInfo.index][cellInfo.column.id],
          }}
        />
      );

      this.Cell = (row) => renderSelect(row);
    }
  }

  class EditableTextColumn extends Column {
    constructor(header, accessor, sortable = true, data = [], setData = () => {}, validateInput) {
      super(header, accessor, sortable);
      this.className = 'wordwrap';
      const renderSelect = (cellInfo) => (
          <div
              style={{ backgroundColor: '#fafafa' }}
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => {
                setData(update(data, {
                  [cellInfo.index]: {
                    [cellInfo.column.id]: {
                      $set: validateInput ? validateInput(e.target.innerText) : e.target.innerText,
                    },
                  },
                }));
              }}
              dangerouslySetInnerHTML={{
                __html: data[cellInfo.index][cellInfo.column.id],
              }}
          />
      );

      this.Cell = (row) => renderSelect(row);
    }
  }

  return {
    NotFilteredNumberColumn,
    NotFilteredPercentageColumn,
    NotFilteredBooleanColumn,
    NotFilteredDateTimeWithSecondsColumn,
    NotFilteredDateTimeColumn,
    NotFilteredDateColumn,
    NotFilteredTextColumn,
    NotFilteredTextCommaColumn,
    NotFilteredDateWithDayColumn,
    CheckboxColumn,
    ActionColumn,
    EditableCheckboxColumn,
    EditableDatePickerColumn,
    FilteredNullableBooleanColumn,
    NotFilteredReadOnlyCheckboxColumn,
    FilteredTextColumn,
    FilteredDateColumn,
    FilteredTextCommaColumn,
    EditableSelectColumn,
    EditableInputColumn,
    EditableTextColumn,
  };
};
